import { apiVersion } from './apis.const';
import { transformResponse } from './apis.util';
import { baseApi } from './base.api';
import type { EDecalVersion } from 'enums';

/**
 * API Urls
 */
const decalIdUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) => `/${orgSlug}/${apiVersion}/decals/${id}`;
const decalApproveUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) =>
  `/${orgSlug}/${apiVersion}/decals/${id}/approve`;
const decalUnapproveUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) =>
  `/${orgSlug}/${apiVersion}/decals/${id}/unapprove`;
const decalBulkApproveUrl = ({ orgSlug }: { orgSlug: string }) => `/${orgSlug}/${apiVersion}/decals/bulk-approve`;

/**
 * Create APIs
 */
export const decalApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    getDecal: build.query<GetDecalRes, GetDecalReq>({
      query: ({ orgSlug, id, version }) => ({
        url: `${decalIdUrl({ orgSlug, id })}?format=svg&version=${encodeURIComponent(version)}`,
        responseHandler: (response) => response.text(),
      }),
      providesTags: (result, error, { id }) => [{ type: 'Zone', id }, 'GetDecal'],
    }),

    downloadDecal: build.query<DownloadDecalRes, DownloadDecalReq>({
      query: ({ orgSlug, id, format }) => ({
        url: `${decalIdUrl({ orgSlug, id })}?format=${format}`,
        headers: {
          Accept: 'application/octet-stream',
        },
        responseHandler: (response) => response.arrayBuffer(),
      }),
    }),

    bulkApproveDecals: build.mutation<BulkApproveDecalRes, BulkApproveDecalReq>({
      query: ({ orgSlug, zone_ids }) => ({
        url: decalBulkApproveUrl({ orgSlug }),
        method: 'POST',
        body: { zone_ids },
      }),
      invalidatesTags: ['Zones'],
    }),

    approveDecal: build.mutation<ApproveDecalRes, ApproveDecalReq>({
      query: ({ orgSlug, id, version }) => ({
        url: `${decalApproveUrl({ orgSlug, id })}?version=${encodeURIComponent(version)}`,
        method: 'POST',
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    unapproveDecal: build.mutation<UnapproveDecalRes, UnapproveDecalReq>({
      query: ({ orgSlug, id }) => ({
        url: decalUnapproveUrl({ orgSlug, id }),
        method: 'DELETE',
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useGetDecalQuery,
  useLazyDownloadDecalQuery,
  useApproveDecalMutation,
  useUnapproveDecalMutation,
  useBulkApproveDecalsMutation,
} = decalApi;

/**
 * Types
 */
type GetDecalRes = any;
type GetDecalReq = { orgSlug: string; id: number; version: EDecalVersion };
type DownloadDecalRes = any;
type DownloadDecalReq = { orgSlug: string; id: number; format: 'svg' | 'jpeg' | 'png' | 'pdf' };
type ApproveDecalRes = any;
type ApproveDecalReq = { orgSlug: string; id: number; version: EDecalVersion };
type UnapproveDecalRes = any;
type UnapproveDecalReq = { orgSlug: string; id: number };
type BulkApproveDecalRes = any;
type BulkApproveDecalReq = { orgSlug: string; zone_ids: number[] };
